import { useState, useEffect } from 'react';
import { Loader2, FileX } from 'lucide-react';
import { useAuthStore } from '../stores/auth-store';

interface AuthenticatedPdfViewerProps {
  src: string;
  title: string;
  className?: string;
}

/**
 * Component that fetches PDFs with authentication headers.
 * Regular <iframe src> doesn't send Authorization headers, causing 401 errors
 * for protected endpoints. This component fetches the PDF with proper
 * auth headers and displays it using a blob URL.
 */
export function AuthenticatedPdfViewer({ src, title, className }: AuthenticatedPdfViewerProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    async function fetchPdf() {
      setLoading(true);
      setError(null);

      try {
        const token = await useAuthStore.getState().getAccessToken();
        const response = await fetch(src, {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load PDF: ${response.status}`);
        }

        const blob = await response.blob();

        if (cancelled) return;

        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load PDF');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchPdf();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src]);

  // Clean up blob URL when component unmounts or src changes
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`}>
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-gray-400 animate-spin mx-auto" />
          <p className="mt-2 text-sm text-gray-500">Loading PDF...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center bg-gray-100 ${className}`}>
        <FileX className="h-12 w-12 text-gray-400 mb-2" />
        <p className="text-sm text-gray-500">{error}</p>
      </div>
    );
  }

  return (
    <iframe
      src={blobUrl || ''}
      title={title}
      className={className}
    />
  );
}
